import { computed, inject, Injectable, signal } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  of,
  Subject,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { Pagination } from '../../../shared/models/models';
import { FilterForm } from '../models/filter-form.models';
import { JobSearchService } from './job-search.service';
import { DefaultPageSize } from '../../../shared/consts/shared.const';
import { toSignal } from '@angular/core/rxjs-interop';
import { DeviceDetectionService } from '../../../core/services/device-detection/device-detection.service';

@Injectable({
  providedIn: 'root',
})
export class QueryService {
  private deviceDetectionService = inject(DeviceDetectionService);
  private jobSearchService = inject(JobSearchService);

  isQueryLoading = new Subject<boolean>();
  filterQuery$: Subject<FilterForm> = new Subject();

  isLoadPagination = signal(false);

  shouldTriggerFilterQueryDirectly = new BehaviorSubject<boolean>(true);
  private shouldTriggerFilterQueryDirectlySignal  = toSignal(
    this.shouldTriggerFilterQueryDirectly.asObservable(),
    { initialValue: true }
  );
  private readonly isMobileDevice = computed(() => this.deviceDetectionService.isMobileOrTablet());
  lastFilterQuery = signal<FilterForm | null>(null);
  private paginationObs = new BehaviorSubject<Pagination>({
    pageNo: 1,
    pageSize: DefaultPageSize,
    total: 0,
  });
  public paginationObs$ = this.paginationObs.asObservable();
  private _pagination!: Pagination;

  private keyword = new BehaviorSubject<string>('');
  public keyword$ = this.keyword.asObservable();

  public isDelayAllowForKeywordSearch = signal(true);

  private searchCriteria$ = combineLatest([
    this.filterQuery$.asObservable(),
    this.paginationObs$,
  ]).pipe(
    debounceTime(100),
    tap(() => {
      if (!(!this.isMobileDevice() || this.shouldTriggerFilterQueryDirectlySignal())) {
        this.isQueryLoading.next(false);
      }
    }),
    filter(() => !this.isMobileDevice() || this.shouldTriggerFilterQueryDirectlySignal()),
    tap((filterData) => {
      this.shouldTriggerFilterQueryDirectly.next(false);
      this.lastFilterQuery.set(filterData[0]);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  filterQueryRes$ = this.searchCriteria$.pipe(
    switchMap(([filterForm, pagination]) => {
      this.keyword.next(filterForm.keyword ?? '');
      return this.jobSearchService.getJobs(
        filterForm,
        pagination.pageNo,
        pagination.pageSize
      ).pipe(
        catchError(() => of({ data: [], premiumData: [] , common: { total_records_found: 0, total_vacancies: 0 } }))
      )
    })
  );

  jobSearchCount$ = this.searchCriteria$.pipe(
    distinctUntilChanged(
      (previous, current) => JSON.stringify(previous[0]) === JSON.stringify(current[0])
    ),
    switchMap(([filterForm, pagination]) =>
      this.jobSearchService.getJobSearchCount(
        filterForm,
        pagination.pageNo,
        pagination.pageSize
      ).pipe(
        catchError((error) => {
          console.error('Failed to fetch job search count', error);
          return of(null);
        })
      )
    )
  );

  public set pagination(p: Pagination) {
    this._pagination = p;
    const pagination = {
      pageNo: this._pagination.pageNo,
      pageSize: this._pagination.pageSize,
    };
    this.paginationObs.next(pagination);
    this.isQueryLoading.next(true);
  }

  public get pagination(): Pagination {
    return this._pagination;
  }

  lastActiveFilters = signal<FilterForm | null>(null);

}
